import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import "./VideoGallery.css"; // Make sure to create and use this CSS file

// --- Data Definition ---
// This is the ONLY place you need to edit to add or remove videos.
const videosData = [
  {
    youtubeId: "_vD25Gfue7I",
    description: "Happy to be associated with MSB Association - Dima Kats, founder of Clear Junction | MSB Association Annual Members Meet 2026",
    event: "Annual Meet 2026",
  },
  {
    youtubeId: "8PaMAfnQ8Jo",
    description: "Can MSBs truly scale across fiat & digital rails without compromising trust? | MSB Association Annual Members Meet 2026",
    event: "Annual Meet 2026",
  },
  {
    youtubeId: "PwiUREaSt6g",
    description: "Stablecoins in Remittance - Are they the future of cross-border payments? | MSB Association Annual Members Meet 2026",
    event: "Annual Meet 2026",
  },
  {
    youtubeId: "ul0W5Q09xLY",
    description: "\"Today's event was really good. I enjoyed it.\" - Mr.Caleb Epelle-Oboko from Prune Payments Ltd",
    event: "Summer Meet 2026",
  },
];

// Set to true to display the Summer Meet 2026 tab and its videos
const ENABLE_SUMMER_MEET = true;

// --- Reusable Video Thumbnail Component ---
// This component manages its own animation state.
const VideoThumbnail = ({ youtubeId, onClick, index, colClass }) => {
  const ref = useRef(null);
  // `once: true` ensures the animation only plays once.
  // `amount: 0.3` means the animation triggers when 30% of the item is visible.
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const thumbnailVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <motion.div
      ref={ref}
      className={`${colClass} col-sm-12 d-flex justify-content-center mb-4`}
      variants={thumbnailVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      transition={{ duration: 0.5, delay: index * 0.1 }} // Staggered delay
      onClick={onClick}
    >
      <div className="video-thumbnail-container w-100">
        <img
          src={`https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`}
          alt="Video thumbnail"
          className="img-fluid gallery-thumb"
        />
        <div className="play-icon">
          <i className="fa-solid fa-play"></i>
        </div>
      </div>
    </motion.div>
  );
};

// --- Main Video Gallery Component ---
const VideoGallery = () => {
  const mainHeadingRef = useRef(null);
  const isHeadingInView = useInView(mainHeadingRef, { once: true });

  const [modalOpen, setModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeEvent, setActiveEvent] = useState(ENABLE_SUMMER_MEET ? "Summer Meet 2026" : "Annual Meet 2026");

  const availableEvents = ENABLE_SUMMER_MEET 
    ? ["Summer Meet 2026", "Annual Meet 2026"] 
    : ["Annual Meet 2026"];

  const filteredVideos = videosData.filter(
    (video) => video.event === activeEvent
  );

  const openModal = (index) => {
    setCurrentIndex(index);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const showNext = (e) => {
    e.stopPropagation(); // Prevent modal from closing when clicking the button
    setCurrentIndex((prev) => (prev + 1) % filteredVideos.length);
  };

  const showPrev = (e) => {
    e.stopPropagation(); // Prevent modal from closing
    setCurrentIndex(
      (prev) => (prev - 1 + filteredVideos.length) % filteredVideos.length,
    );
  };

  const headingVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const badgeItem = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const containerVariants = {
    visible: { transition: { staggerChildren: 0.2 } },
  };

  // Determine column class based on video count
  const getColClass = () => {
    const count = filteredVideos.length;
    if (count === 1) return "col-lg-4 col-md-6";
    if (count === 2) return "col-lg-6 col-md-6";
    return "col-lg-4 col-md-6";
  };

  const colClass = getColClass();

  return (
    <section
      className="service_section section_space section_decoration"
      style={{ overflowX: "hidden" }}
    >
      <div className="container">
        <div className="heading_block text-center" ref={mainHeadingRef}>
          <motion.div
            className="badge style_2 text-uppercase"
            variants={containerVariants}
            initial="hidden"
            animate={isHeadingInView ? "visible" : "hidden"}
          >
            <motion.span variants={badgeItem}>
              <i className="fa-solid fa-circle text-primary"></i> Our Gallery{" "}
              <i className="fa-solid fa-circle text-primary"></i>
            </motion.span>
          </motion.div>
          <motion.h2
            variants={headingVariants}
            initial="hidden"
            animate={isHeadingInView ? "visible" : "hidden"}
            className="heading_text mb-0"
          >
            Video Gallery 2026
          </motion.h2>
        </div>

        {availableEvents.length > 1 && (
          <div className="event-tabs-wrapper mt-4 mb-2 text-center">
            <div className="event-tabs-pill">
              {availableEvents.map((evt) => (
                <button
                  key={evt}
                  className={`event-tab-btn ${activeEvent === evt ? "active" : ""}`}
                  onClick={() => setActiveEvent(evt)}
                >
                  {evt}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="row align-items-center mt-4 justify-content-center">
          {filteredVideos.map((video, index) => (
            <VideoThumbnail
              key={video.youtubeId} // YouTube IDs are great unique keys
              youtubeId={video.youtubeId}
              index={index}
              colClass={colClass}
              onClick={() => openModal(index)}
            />
          ))}
          {filteredVideos.length === 0 && (
            <div className="col-12 text-center py-4">
              <p className="no_videos_text text-muted">No videos available for this event yet.</p>
            </div>
          )}
        </div>

        <div className="text-center mt-5">
          <NavLink
            className="btn bg-primary text-dark fw-bold px-5"
            to="/video-gallery-2026"
          >
            <span className="btn_label">View All Videos</span>
          </NavLink>
        </div>
      </div>

      {/* Modal for Video Player */}
      {modalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="video-responsive">
              <iframe
                width="853"
                height="450"
                src={`https://www.youtube.com/embed/${filteredVideos[currentIndex]?.youtubeId}?autoplay=1&rel=0`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Embedded youtube"
              />
            </div>

            {filteredVideos[currentIndex]?.description && (
              <p className="modal-description">
                {filteredVideos[currentIndex]?.description}
              </p>
            )}

            <button className="modal-close" onClick={closeModal}>
              ×
            </button>
            <button className="modal-prev" onClick={showPrev}>
              ❮
            </button>
            <button className="modal-next" onClick={showNext}>
              ❯
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default VideoGallery;
